import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Link } from 'react-router-dom';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Search, ExternalLink, FileText, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/lib/auth';

// Owner + operations + design managers only — matches backend gate.
const CAN_SEE = new Set(['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture']);

export default function AdminSopNeedsConfirmationPage() {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const canImport = user?.role === 'owner';

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/sop_library/needs_confirmation');
      setPages(r.data.pages || []);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const set = new Set(pages.map(p => p.sop_category).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [pages]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return pages.filter(p => {
      if (category !== 'all' && p.sop_category !== category) return false;
      if (!ql) return true;
      const hay = [p.title, p.sop_code, p.sop_owner, ...(p.markers || [])].join(' ').toLowerCase();
      return hay.includes(ql);
    });
  }, [pages, q, category]);

  const rerunImport = async () => {
    if (!window.confirm('Re-run the SOP Library import? Existing SOP comments will be preserved.')) return;
    setRerunning(true);
    try {
      const r = await api.post('/admin/sop_library/import');
      toast.success(`Import done: ${r.data.total_sops} SOPs, replaced ${r.data.replaced.length}, created ${r.data.created.length}, deleted ${r.data.deleted.length}.`);
      load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setRerunning(false); }
  };

  if (!CAN_SEE.has(user?.role)) {
    return (
      <AppShell breadcrumbs={[{ label: 'Admin' }, { label: 'Needs confirmation' }]}>
        <div className="p-6 text-[hsl(var(--foreground-muted))]" data-testid="sop-needs-noaccess">Owner or leads only.</div>
      </AppShell>
    );
  }

  return (
    <AppShell breadcrumbs={[{ label: 'Admin' }, { label: 'Needs confirmation' }]}>
      <div className="p-6 max-w-[1100px] mx-auto space-y-4 fade-up" data-testid="sop-needs-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <AlertTriangle size={22} />SOPs awaiting confirmation
            </h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[720px]">
              Every SOP page below contains one or more <code className="font-mono text-[12px]">[CONFIRM — …]</code> markers.
              Open the page, confirm with the SOP owner, and either fill in the specific value or remove the marker in an edit.
            </p>
          </div>
          {canImport && (
            <Button size="sm" variant="outline" onClick={rerunImport} disabled={rerunning} data-testid="sop-rerun-import">
              <RefreshCcw size={13} className="mr-1" />
              {rerunning ? 'Re-running…' : 'Re-run SOP import'}
            </Button>
          )}
        </div>

        <div className="card p-3 flex items-center gap-2 flex-wrap" data-testid="sop-needs-filters">
          <Search size={14} className="text-[hsl(var(--foreground-muted))]" />
          <Input placeholder="Search by title, code, owner, or marker text…"
                 value={q} onChange={e => setQ(e.target.value)}
                 className="w-full sm:w-[320px]" data-testid="sop-needs-search" />
          <select className="h-9 border rounded-md px-2 text-[13px]"
                  style={{ borderColor: 'hsl(var(--border))' }}
                  value={category} onChange={e => setCategory(e.target.value)}
                  data-testid="sop-needs-category">
            {categories.map(c => (
              <option key={c} value={c}>{c === 'all' ? 'All categories' : `Category ${c}`}</option>
            ))}
          </select>
          <span className="text-[12.5px] text-[hsl(var(--foreground-muted))] ml-auto" data-testid="sop-needs-count">
            {filtered.length} of {pages.length} page(s)
          </span>
        </div>

        {loading && <div className="text-center py-6 text-[hsl(var(--foreground-muted))] text-[13px]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-10 border rounded-lg text-[hsl(var(--foreground-muted))] text-[13.5px]"
               style={{ borderColor: 'hsl(var(--border))' }} data-testid="sop-needs-empty">
            Nothing awaiting confirmation. Every SOP is clean.
          </div>
        )}

        <div className="space-y-2">
          {filtered.map(p => (
            <Link key={p.page_id} to={`/knowledge/${p.page_id}`}
                  className="card p-3 flex items-start gap-3 hover:border-[hsl(var(--border-strong))]"
                  data-testid={`sop-needs-row-${p.sop_code}`}>
              <FileText size={16} className="mt-0.5 flex-none text-[hsl(var(--foreground-muted))]" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="chip">{p.sop_code}</span>
                  <span className="font-medium">{p.title}</span>
                  <span className="chip chip-warning inline-flex items-center gap-1">
                    <AlertTriangle size={10} />{p.markers?.length || 1} marker{(p.markers?.length || 1) === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5">
                  Category {p.sop_category}{p.sop_owner ? ` · Owner: ${p.sop_owner}` : ''}
                </div>
                {(p.markers || []).slice(0, 3).map((m, i) => (
                  <div key={i} className="text-[12.5px] text-[hsl(var(--foreground-muted))] italic mt-1 line-clamp-1"
                       data-testid={`sop-needs-marker-${p.sop_code}-${i}`}>
                    &ldquo;{m.length > 180 ? m.slice(0, 180) + '…' : m}&rdquo;
                  </div>
                ))}
                {(p.markers?.length || 0) > 3 && (
                  <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-0.5">
                    + {p.markers.length - 3} more
                  </div>
                )}
              </div>
              <ExternalLink size={14} className="mt-1 text-[hsl(var(--foreground-subtle))] flex-none" />
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
